import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ClipboardList, Clock, CheckCircle2, AlertTriangle, RefreshCw, CirclePlus } from 'lucide-react';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import { stockReconciliationService } from '../../services/stockReconciliationService';
import {
  StockReconciliationListDTO,
  StockReconciliationStatus,
  StockReconciliationStatsDTO,
} from '../../types';
import './StockReconciliationPage.css';

// ─── Component ────────────────────────────────────────────────────────────────

const StockReconciliationListPage: React.FC = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { t }     = useTranslation();

  const STATUS_LABEL: Record<StockReconciliationStatus, string> = {
    Draft:           t('statuses.draft'),
    PendingApproval: t('statuses.pendingApproval'),
    Approved:        t('statuses.approved'),
    Rejected:        t('statuses.rejected'),
    Posted:          t('statuses.posted'),
    Cancelled:       t('statuses.cancelled'),
  };

  const STATUS_CLASS: Record<StockReconciliationStatus, string> = {
    Draft:           'ims-badge sr-badge-draft',
    PendingApproval: 'ims-badge sr-badge-pending',
    Approved:        'ims-badge sr-badge-approved',
    Rejected:        'ims-badge sr-badge-rejected',
    Posted:          'ims-badge sr-badge-posted',
    Cancelled:       'ims-badge sr-badge-cancelled',
  };

  const TYPE_CLASS: Record<string, string> = {
    Daily:   'ims-badge sr-type-daily',
    Weekly:  'ims-badge sr-type-weekly',
    Monthly: 'ims-badge sr-type-monthly',
    Adhoc:   'ims-badge sr-type-adhoc',
  };

  const [reconciliations, setReconciliations] = useState<StockReconciliationListDTO[]>([]);
  const [stats, setStats]                     = useState<StockReconciliationStatsDTO | null>(null);
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState<string | null>(null);
  const [successMsg, setSuccessMsg]           = useState<string | null>(
    (location.state as any)?.message || null
  );

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!successMsg) return;
    const timer = setTimeout(() => setSuccessMsg(null), 4000);
    return () => clearTimeout(timer);
  }, [successMsg]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [listRes, statsRes] = await Promise.all([
        stockReconciliationService.getAll(1, 100),
        stockReconciliationService.getStats(),
      ]);
      const payload = listRes.data?.data;
      setReconciliations(payload?.data ?? []);
      if (statsRes.data?.data) setStats(statsRes.data.data);
    } catch {
      setError(t('stockReconciliation.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const columns: GridColumn[] = [
    {
      key: 'reconciliationNumber',
      label: t('stockReconciliation.reconciliationNumber'),
      sortable: true,
      width: '170px',
    },
    {
      key: 'reconciliationDate',
      label: t('stockReconciliation.reconciliationDate'),
      width: '130px',
      render: (v) => new Date(v as string).toLocaleDateString(),
    },
    {
      key: 'reconciliationType',
      label: t('stockReconciliation.reconciliationType'),
      width: '110px',
      render: (v) => {
        const type = v as string;
        return <span className={TYPE_CLASS[type] || 'ims-badge'}>{type}</span>;
      },
    },
    { key: 'warehouseName',   label: t('stockReconciliation.warehouse'),   width: '160px' },
    {
      key: 'status',
      label: t('common.status'),
      width: '150px',
      render: (v) => {
        const s = v as StockReconciliationStatus;
        return <span className={STATUS_CLASS[s] || 'ims-badge'}>{STATUS_LABEL[s] ?? s}</span>;
      },
    },
    {
      key: 'varianceItemCount',
      label: t('stockReconciliation.varianceItems'),
      width: '110px',
      render: (v) => {
        const count = v as number;
        return (
          <span className={count > 0 ? 'variance-negative' : 'variance-zero'}>
            {count}
          </span>
        );
      },
    },
    { key: 'itemCount',      label: t('stockReconciliation.itemCount'),  width: '80px' },
    { key: 'countedByName',  label: t('stockReconciliation.countedBy'),  width: '140px' },
    {
      key: 'createdAt',
      label: t('common.createdAt'),
      width: '120px',
      render: (v) => new Date(v as string).toLocaleDateString(),
    },
  ];

  const actions: GridAction[] = [
    {
      label: t('common.view'),
      onClick: (row) => navigate(`/stock-reconciliations/${row.id}`),
      className: 'grid-action-btn-view',
    },
    {
      label: t('common.edit'),
      onClick: (row) => navigate(`/stock-reconciliations/${row.id}/edit`),
      className: 'grid-action-btn-edit',
      hidden: (row) => row.status !== 'Draft',
    },
  ];

  return (
    <Layout>
      <div className="ims-page sr-page">
        {/* Header */}
        <div className="ims-list-header">
          <h1 className="ims-list-title">{t('stockReconciliation.title')}</h1>
          <div className="ims-list-actions">
            <button
              className="btn-refresh"
              onClick={loadData}
              disabled={loading}
            >
              {loading ? <RefreshCw size={14} strokeWidth={2} /> : t('common.refresh')}
            </button>
            <button
              className="btn-new"
              onClick={() => navigate('/stock-reconciliations/create')}
            >
              <CirclePlus/>{t('stockReconciliation.newReconciliation')}
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="ims-stats-row">
            <div className="ims-stat-card">
              <div className="ims-stat-icon sr-stat-icon-total"><ClipboardList size={22} strokeWidth={1.75} /></div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{stats.totalReconciliations}</span>
                <span className="ims-stat-label">{t('stockReconciliation.totalReconciliations')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-icon sr-stat-icon-pending"><Clock size={22} strokeWidth={1.75} /></div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{stats.pendingApproval}</span>
                <span className="ims-stat-label">{t('stockReconciliation.pendingApproval')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-icon sr-stat-icon-posted"><CheckCircle2 size={22} strokeWidth={1.75} /></div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{stats.posted}</span>
                <span className="ims-stat-label">{t('stockReconciliation.posted')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-icon sr-stat-icon-variance"><AlertTriangle size={22} strokeWidth={1.75} /></div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{stats.withVariance}</span>
                <span className="ims-stat-label">{t('stockReconciliation.withVariance')}</span>
              </div>
            </div>
          </div>
        )}

        {successMsg && <div className="ims-alert ims-alert-success">{successMsg}</div>}
        {error      && <div className="ims-alert ims-alert-error">{error}</div>}

        <div className="ims-grid-wrapper">
          <DataGrid
            columns={columns}
            data={reconciliations}
            loading={loading}
            actions={actions}
            emptyMessage={t('stockReconciliation.noReconciliations')}
          />
        </div>
      </div>
    </Layout>
  );
};

export default StockReconciliationListPage;
